<template>
  <ModalDialog
    header-text="Ingredient Details"
    contentClass="ingredient-details-content"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <div class="label-name">{{ ingredient.ScheduleALabelName }}</div>
      <div class="flex-container">
        <Grid
          :data-items="ingredientProperties"
          :columns="columns"
          :scrollable="'none'"
          data-cy="schedule-a-details-grid"
        >
          <template v-slot:emptyHeader="{}"> </template>
        </Grid>
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import { EnumDpm } from "@/Domain/EnumDpm";
import type { IngredientDisplay } from "@/Domain/IngredientDisplay";
import { Grid, type GridColumnProps } from "@progress/kendo-vue-grid";
import { ref } from "vue";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { EnumMerchStatus } from "@/Domain/EnumMerchStatus";
import { convertNumber } from "@/Domain/EnumGroupConverter";
import { EnumUsedIn1Status } from "@/Domain/EnumUsedIn1Status";
import { useMainStore } from "@/stores/useMainStore";

const main = useMainStore();

const props = defineProps<{
  ingredient: IngredientDisplay;
}>();

const emit = defineEmits(["closeModal"]);

const ingredientProperties = ref<{ name: string; value: string }[]>([]);

const columns: GridColumnProps[] = [
  {
    field: "name",
    headerCell: "emptyHeader",
    className: "name-row",
  },
  {
    field: "value",
    headerCell: "emptyHeader",
  },
];

const setIngredientProperties = function () {
  ingredientProperties.value = [
    { name: "Ingredient Number", value: props.ingredient.ScheduleALabelNumber },
    {
      name: "Alternate Name",
      value: props.ingredient.VueScheduleAAlternateName,
    },
    {
      name: "Brand Name",
      value: props.ingredient.VueScheduleABrandName,
    },
    { name: "Source Company", value: props.ingredient.DistributorCompanyName },
    {
      name: "Raw Material Code",
      value: props.ingredient.Rmc,
    },
    {
      name: "Group",
      value: convertNumber(Number(props.ingredient.VueScheduleAGroup)),
    },
    { name: "Symbol", value: props.ingredient.Symbol },
    {
      name: "DPM",
      value: props.ingredient._Dpm ? EnumDpm[props.ingredient._Dpm] : "",
    },
    {
      name: "Passover Always",
      value: props.ingredient._Pesach ? "Y" : "N",
    },
    {
      name: "Merchandise ID",
      value: props.ingredient.MerchandiseId?.toString() ?? "",
    },
    {
      name: "UKD ID",
      value: props.ingredient.UkdId,
    },
    {
      name: "Ingredient Status",
      value: props.ingredient._LabelStatus
        ? EnumMerchStatus[props.ingredient._LabelStatus]
        : "",
    },
    {
      name: "Ingredient Status in Plant",
      value:
        props.ingredient.VueScheduleAUsedInOneStatus != null //maybe the 0 value is evaluating to non existant and falling into the else condition b/c the top value of 0 ie. Allowed is not evaluating properly
          ? EnumUsedIn1Status[props.ingredient.VueScheduleAUsedInOneStatus]
          : "",
    },
    {
      name: "Internal Code",
      value: props.ingredient.InternalCode,
    },
    {
      name: "Special Requirements", // includes merch and label stipulation
      value: props.ingredient.VueMerchAndLabelCommentsScheduleA,
    },
    {
      name: "Seal or Signature Required",
      value: props.ingredient.VueScheduleASealSign,
    },
    {
      name: "Plant Specific Comment",
      value: props.ingredient.VuePlantSpecificComment,
    },
  ];

  if (main.isEmulator) {
    ingredientProperties.value.push({
      name: "Certified Until",
      value: props.ingredient.CertifiedUntil
        ? new Date(props.ingredient.CertifiedUntil).toLocaleDateString()
        : "",
    });
  }
};

setIngredientProperties();

const closeModal = function () {
  emit("closeModal");
};
</script>

<style scoped lang="scss">
.label-name {
  margin: 20px 20px 10px;
  font-weight: bold;
  max-width: 460px;
}

.flex-container {
  display: flex;
  flex-wrap: nowrap;
  max-width: 500px;
  margin-top: 10px;
  margin-bottom: 20px;

  td {
    font-size: 13px;
  }
}
</style>

<style lang="scss">
.ingredient-details-content {
  .flex-container .k-grid-header {
    display: none !important;
  }
}
</style>
